import { paintIconAction } from './82-icon-actions.mjs';
import { getBobBackdropUrl } from './74-bob-renderer.mjs';

/** Arcade dock with in-stage utility regions and independent sound/pause.
 * Opening pauses play; one region opens at a time. Escape returns focus to
 * its native button. These regions never trap focus or create browser dialogs. */
export function createBobPanels(ctx, { stage, pause, listen, onOpen }) {
  const toolbar = ctx.e('div', 'bob-toolbar');
  toolbar.setAttribute('aria-label', 'Game controls');
  toolbar.setAttribute('role', 'group');
  const panels = [];
  function panel(name, icon, label) {
    const button = ctx.e('button', `bob-${name}`);
    button.type = 'button';
    paintIconAction(ctx, button, icon, label);
    button.setAttribute('aria-expanded', 'false');
    const content = ctx.e('section', `bob-panel-content bob-panel-${name}`);
    content.hidden = true;
    content.setAttribute('aria-label', label);
    const heading = ctx.e('header', 'bob-panel-heading');
    const close = ctx.e('button', 'game-action bob-panel-close');
    close.type = 'button';
    paintIconAction(ctx, close, 'x', `Close ${label}`);
    heading.append(ctx.e('h3', '', label), close);
    content.append(heading);
    const entry = { button, content };
    panels.push(entry);
    const setOpen = (open) => {
      content.hidden = !open;
      button.setAttribute('aria-expanded', String(open));
    };
    entry.setOpen = setOpen;
    const dismiss = () => {
      setOpen(false);
      button.focus?.({ preventScroll: true });
    };
    listen(close, 'click', dismiss);
    const escape = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        dismiss();
      }
    };
    listen(content, 'keydown', escape);
    listen(button, 'keydown', escape);
    listen(button, 'click', () => {
      if (!content.hidden) return dismiss();
      for (const other of panels) other.setOpen(false);
      onOpen();
      setOpen(true);
      close.focus?.({ preventScroll: true });
    });
    toolbar.append(button);
    stage.append(content);
    return entry;
  }
  const sound = ctx.e('button', 'game-action bob-sound');
  sound.type = 'button';
  const settings = panel('options', 'settings', 'Settings');
  const help = panel('help', 'circle-help', 'How to play');
  const records = panel('records-panel', 'trophy', 'Your records');
  help.content.append(
    ctx.e(
      'p',
      '',
      'Each world changes movement. Read its tip before starting; pause to see it again. Gold spring corridors keep a steady flight arc in every world.',
    ),
  );
  const art = ctx.e('img', 'bob-help-art');
  art.src = getBobBackdropUrl(1);
  art.alt = 'Bob’s colourful picnic world, with hills and an open path to explore';
  art.width = 640;
  art.height = 360;
  art.loading = 'lazy';
  help.content.append(art, ctx.e('h4', '', 'Big bounces. Brave routes. Better rewards.'));
  for (const [title, copy] of [
    ['1 · Steer', 'Hold left or right, or drag the scene. Release to brake sideways.'],
    ['2 · Bounce', 'Press Bounce or Space just before landing. Follow the landing ring.'],
    [
      '3 · Explore',
      'Aim for striped trampolines and gold crate arcs. Later routes need mid-air steering and pay more.',
    ],
    [
      'Keep the chain alive',
      'Collect goodies, smash crates, time good bounces and explore routes within the chain meter window. Your chain earns extra points up to ×4. A crate can send a visible reaction through nearby goodies. Each goodie is collected once. Pausing freezes the meter; a stumble breaks the chain.',
    ],
    [
      'Crates',
      'Wood breaks on contact. Reinforced stripes need speed. Cyan spring crates bounce you onward.',
    ],
    [
      'Make it yours',
      'Finish each world to unlock the next. Replay unlocked worlds from the start or pause screen. Timing assist is in Settings, and assisted finishes still unlock worlds. Gates save a recovery point; Restart begins a clean run.',
    ],
    [
      'Keep your world tour',
      'Worlds and medals stay in this session. Turn on Keep my progress in Your records to save them on this shared device. Clear device records starts your world tour again from World 1.',
    ],
    [
      'Finish high',
      'Your final bounce earns 100 points per metre of peak height at the finish. Reach 6m for ×2 or 8m for ×3 on that bonus. The award locks at the line; later taps cannot add points.',
    ],
    [
      'Medals',
      'Food and crate goodies protect you: a hit drops them all but keeps your other points. A hit with no goodies ends the run. After a drop you have a brief recovery window. Finish earns a medal. Clear two bonus routes for Explorer. Mastery needs ten perfect bounces, no assist, recovery or stumbles, and a finish under par.',
    ],
  ])
    help.content.append(ctx.e('h4', '', title), ctx.e('p', '', copy));
  stage.append(pause, sound, toolbar);
  return {
    sound,
    setPlaying(playing) {
      toolbar.hidden = playing;
      if (playing) for (const panel of panels) panel.setOpen(false);
    },
    options: settings.content,
    records: records.content,
    close() {
      for (const panel of panels) panel.setOpen(false);
    },
  };
}
